// Widget: Diese Woche fällig — offene Tasks (Projekt + persönlich) deren Due
// in der aktuellen ISO-Woche (Mo-So) liegt.

import { useEffect, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Calendar } from "lucide-preact";
import { fetchMyOpenTasks, type DashboardTaskItem } from "../../lib/dashboardTasks";
import { formatDateShort, localDateIso } from "../../lib/format";

function weekBounds(): { start: string; end: string } {
  // Monday-based ISO week.
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // 0=Mo, 6=So
  const start = new Date(now);
  start.setDate(now.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: localDateIso(start), end: localDateIso(end) };
}

export function ThisWeekWidget(): preact.JSX.Element {
  const [tasks, setTasks] = useState<DashboardTaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const { start, end } = weekBounds();
    void fetchMyOpenTasks().then(
      (items) => {
        if (cancelled) return;
        const filtered = items.filter((t) => t.due !== null && t.due >= start && t.due <= end);
        setTasks(filtered.slice(0, 5));
        setLoading(false);
      },
      () => {
        // Sonst blieb ein Ladefehler unsichtbar hinter "nichts fällig" — ein
        // leerer Erfolg sieht identisch aus wie ein Fehlschlag, ohne diesen
        // eigenen Zustand.
        if (cancelled) return;
        setError(true);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading)
    return <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>;
  if (error)
    return (
      <p class="text-xs text-[color:var(--color-error)] italic">
        Konnte nicht geladen werden.
      </p>
    );
  if (tasks.length === 0)
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
        Diese Woche nichts fällig.
      </p>
    );

  return (
    <ul class="flex flex-col gap-1.5 text-sm">
      {tasks.map((t) => (
        <li key={t.uid}>
          <button
            type="button"
            onClick={() => setLocation(t.href)}
            class="w-full flex items-center gap-2 text-left rounded px-1.5 py-1 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          >
            <Calendar
              size={14}
              class="text-[color:var(--color-on-surface-variant)] shrink-0"
            />
            <span class="flex-1 min-w-0 truncate">{t.title}</span>
            {t.due && (
              <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)]">
                {formatDateShort(t.due)}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
